'use client';
import { useEffect, useState } from 'react';
import YouTube, { YouTubeProps } from 'react-youtube';
import Image from 'next/image';
import { cn } from '@/components/utils/utils';

export default function WavyYoutubeBanner(props: {
  data: {
    image: {
      url: string;
      alt: string;
      placeholderUrl?: string;
    };
    mobileImage?: {
      url: string;
      alt: string;
      placeholderUrl?: string;
    };
    video?: {
      video: {
        id: string;
      };
      previewTiming: {
        start: number;
        end: number;
      };
    };
  };
}) {
  const image = props.data.image;
  const mobileImage = props.data.mobileImage || props.data.image; // Fallback to regular image if mobile not provided
  const video = props.data.video;
  const [isPlaying, setIsPlaying] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const onPlayerReady: YouTubeProps['onReady'] = (event) => {
    event.target.playVideo();
  };

  const opts: YouTubeProps['opts'] = {
    height: '100%',
    width: '100%',
    playerVars: {
      autoplay: 1,
      controls: 1,
      modestbranding: 1,
      rel: 0,
      start: video?.previewTiming?.start || 0,
    },
  };

  const togglePlayback = () => {
    if (video?.video?.id && !isTransitioning) {
      setIsTransitioning(true);
      setIsPlaying(!isPlaying);
      setTimeout(() => setIsTransitioning(false), 600);
    }
  };

  useEffect(() => {
    const handleEscKeyPress = (event: KeyboardEvent) => {
      if ((event.key === 'Escape' || event.key === 'Esc') && isPlaying) {
        setIsPlaying(false);
      }
    };

    window.addEventListener('keydown', handleEscKeyPress);
    return () => window.removeEventListener('keydown', handleEscKeyPress);
  }, [isPlaying]);

  return (
    <>
      <section
        className={cn(
          'relative z-40 scroll-m-16 bg-gradient-to-b pt-5 sm:pt-0',
          'md:-mt-10',
          '-mt-16 sm:-mt-16 md:-mt-16',
          isPlaying
            ? '-mb-52 md:-mb-12 lg:-mb-20 xl:-mb-20 2xl:-mb-20'
            : '-mb-40 md:-mb-20'
        )}
      >
        <div
          className={cn(
            'relative mx-auto flex justify-center overflow-hidden pt-10 md:pt-0',
            'h-[30rem] md:h-[40rem] lg:h-[50rem]'
          )}
        >
          <svg
            className='pointer-events-none absolute'
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 1471 633'
            preserveAspectRatio='none'
          >
            <defs>
              <clipPath
                id='curved-top-bottom'
                clipPathUnits='objectBoundingBox'
              >
                <path
                  className='hidden md:block'
                  d='M 1 0.0221 C 0.9993 0.0223 0.9972 0.0228 0.9948 0.0231 C 0.9556 0.0300 0.7837 0.0606 0.5007 0.0203 C 0.2182 -0.0200 0.0479 0.0116 0.0086 0.0188 L 0.0086 0.0188 C 0.0039 0.0197 0.0011 0.0203 0.0002 0.0203 L 0.0002 0.2524 L 0 0.2645 V 0.9801 C 0.0436 0.9729 0.2076 0.9504 0.5007 0.9821 C 0.7891 1.0134 0.9554 0.9931 1 0.9852 V 0.0221 Z'
                />
                <path
                  className='block md:hidden'
                  d='M0 0.0365 C 0.0129 0.0365 0.4227 -0.0476 1 0.0395 V 1 C 0.6541 0.9485 0.3443 0.9485 0 1 L 0 0.0365 Z'
                />
              </clipPath>
            </defs>
          </svg>

          <div
            className={cn(
              'sticky top-0 flex items-center justify-center',
              'w-full bg-black sm:[clip-path:_url(#curved-top-bottom)]',
              isPlaying ? 'pb-16 pt-16 sm:pb-24 sm:pt-24 md:pb-32 md:pt-32' : ''
            )}
          >
            {!isPlaying ? (
              // Image view with mobile/desktop variants
              <div
                className='h-full w-full cursor-pointer'
                onClick={togglePlayback}
              >
                {/* Mobile Image - shown only on smallest screens */}
                <Image
                  src={mobileImage?.url}
                  alt={mobileImage?.alt || ''}
                  width={1464}
                  height={350}
                  className='block h-full w-full object-cover sm:hidden'
                  priority
                />

                {/* Desktop Image - hidden on mobile, visible on sm and up */}
                <Image
                  src={image?.url}
                  alt={image?.alt}
                  width={1464}
                  height={350}
                  className='hidden h-full w-full object-fill transition-all duration-500 hover:scale-[1.01] sm:block'
                  priority
                />
              </div>
            ) : (
              // Video view
              <div className='relative flex h-[90%] w-full items-center justify-center'>
                {video?.video?.id && (
                  <>
                    <YouTube
                      videoId={video.video.id}
                      opts={opts}
                      className='h-full w-full'
                      iframeClassName='h-full w-full'
                      onReady={onPlayerReady}
                      onEnd={() => setIsPlaying(false)}
                    />

                    {/* Close button */}
                    <button
                      onClick={togglePlayback}
                      className='absolute right-4 top-4 z-30 rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-black/70 md:-top-16'
                      aria-label='Close video'
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='24'
                        height='24'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      >
                        <line x1='18' y1='6' x2='6' y2='18'></line>
                        <line x1='6' y1='6' x2='18' y2='18'></line>
                      </svg>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <div
          className={cn(
            'container-padding mb w-full transform items-center justify-center pb-28 md:absolute md:bottom-0 md:mb-0 md:flex md:pb-0',
            'translate-y-[-15%] sm:translate-y-[-50%] lg:translate-y-[50%]'
          )}
        ></div>
      </section>
    </>
  );
}
